import { Box, type BoxProps } from '@chakra-ui/react';
import { QRCodeSVG } from 'qrcode.react';
import { MARK_PATH } from './Logo';

// Every QR code Twinden shows carries the mark in its centre, the way
// WhatsApp's does (D261). The code is drawn on the device at error
// correction level H, which survives 30 % of its modules being unreadable;
// the badge covers a fifth of the code's width (4 % of its area, white
// margin included), so the data underneath is recoverable and the overlay
// never has to be baked into the code itself. The modules are the light
// theme's ink on a card that stays white in both themes: scanners prefer it.

/** Error correction level for every Twinden QR code (D261). */
export const QR_LEVEL = 'H';
/** Quiet zone, in modules, drawn inside the card's padding. */
export const QR_QUIET_ZONE = 2;
/** The badge's side as a fraction of the code's width (quiet zone included). */
export const QR_BADGE_FRACTION = 0.2;
/** Corner radius of the badge as a fraction of its side. */
export const QR_BADGE_RADIUS = 0.18;

const INK = '#1F1D1B';
const OXBLOOD = '#7A1F2B';
const SAFFRON = '#F0B63F';

interface TwindenQrProps extends Omit<BoxProps, 'children'> {
  /** The text the code carries: a Twinden link. */
  value: string;
  /** The card's outer size (the code is inset by the padding). */
  size?: BoxProps['boxSize'];
  /** What a screen reader says for the code. */
  label: string;
}

export function TwindenQr({
  value,
  size = '240px',
  label,
  ...rest
}: TwindenQrProps) {
  return (
    <Box
      role="img"
      aria-label={label}
      data-testid="twinden-qr"
      boxSize={size}
      flexShrink={0}
      p={2}
      bg="white"
      borderRadius="20px"
      borderWidth="1px"
      borderColor="line"
      {...rest}
    >
      <Box position="relative" w="full" h="full">
        <QRCodeSVG
          value={value}
          level={QR_LEVEL}
          marginSize={QR_QUIET_ZONE}
          bgColor="#FFFFFF"
          fgColor={INK}
          size={256}
          aria-hidden="true"
          style={{ width: '100%', height: '100%', display: 'block' }}
        />
        {/* The badge is an overlay, so the code underneath stays whole. */}
        <Box
          aria-hidden="true"
          data-testid="twinden-qr-badge"
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          style={{
            width: `${QR_BADGE_FRACTION * 100}%`,
            height: `${QR_BADGE_FRACTION * 100}%`,
            borderRadius: `${QR_BADGE_RADIUS * 100}%`,
          }}
          bg="white"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <svg
            viewBox="0 0 96 96"
            width="76%"
            height="76%"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d={MARK_PATH}
              fill={OXBLOOD}
              stroke={OXBLOOD}
              strokeWidth="4"
              strokeLinejoin="round"
            />
            <circle cx="48" cy="52" r="9" fill={SAFFRON} />
          </svg>
        </Box>
      </Box>
    </Box>
  );
}
